import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Upload, Trash2, Camera, Loader2, Filter, AlertTriangle, Sparkles, Square, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PhotoMarkupDialog } from "./PhotoMarkupDialogEnhanced";
import { useFileUpload } from "@/hooks/useFileUpload";

interface Photo {
  id: string;
  url: string;
  filename: string;
  storage_path: string;
  ai_damage_detected: boolean;
  ai_damage_types: string[];
  ai_analysis_result?: any;
  created_at: string;
  pins?: PinMarker[];
}

interface PinMarker {
  id: string;
  x: number;
  y: number;
  label: string;
  color: string;
  aiPredictionIndex?: number;
}

interface PhotosTabProps {
  jobId: string;
  photos: Photo[];
  onPhotosUpdate: () => void;
}

export const PhotosTab = ({ jobId, photos, onPhotosUpdate }: PhotosTabProps) => {
  const { orgId } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, uploading } = useFileUpload(orgId || '');
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [filter, setFilter] = useState<"all" | "damage" | "clean">("all");
  const [markupDialogOpen, setMarkupDialogOpen] = useState(false);
  const [currentMarkupPhoto, setCurrentMarkupPhoto] = useState<Photo | null>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !orgId) return;

    try {
      for (const file of Array.from(files)) {
        const result = await uploadFile(file, {
          bucket: 'job-photos',
          folder: jobId,
        });

        if (!result) continue;

        const { error: dbError } = await supabase.from("photos").insert({
          job_id: jobId,
          org_id: orgId,
          filename: file.name,
          storage_path: result.path,
          url: result.url,
        });

        if (dbError) throw dbError;
      }

      toast.success(`${files.length} photo(s) uploaded`);
      onPhotosUpdate();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: any) {
      console.error("Error uploading photos:", error);
      toast.error("Failed to upload photos");
    }
  };

  const handleDeletePhoto = async (e: React.MouseEvent, photoId: string, storagePath: string) => {
    e.stopPropagation();
    try {
      const { error: storageError } = await supabase.storage
        .from("job-photos")
        .remove([storagePath]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from("photos")
        .delete()
        .eq("id", photoId);

      if (dbError) throw dbError;

      toast.success("Photo deleted");
      onPhotosUpdate();
    } catch (error: any) {
      console.error("Error deleting photo:", error);
      toast.error("Failed to delete photo");
    }
  };

  const handlePhotoClick = (photo: Photo) => {
    // Always open in fullscreen markup mode
    setCurrentMarkupPhoto(photo);
    setMarkupDialogOpen(true);
    // Also set for heatmap
    setSelectedPhoto(photo);
  };

  const handleSaveAnnotations = async (updatedPhoto: Photo) => {
    try {
      const { error } = await supabase
        .from("photos")
        .update({
          ai_analysis_result: {
            ...filteredPhotos.find(p => p.id === updatedPhoto.id)?.ai_analysis_result,
            pins: updatedPhoto.pins,
          },
        } as any)
        .eq("id", updatedPhoto.id);

      if (error) throw error;
      
      toast.success("Pins saved");
      onPhotosUpdate();
    } catch (error: any) {
      console.error("Error saving pins:", error);
      toast.error("Failed to save pins");
    }
  };

  const handleAnalyzePhotos = async () => {
    if (!orgId) {
      toast.error("Organization ID not found");
      return;
    }

    if (photos.length === 0) {
      toast.error("No photos to analyze");
      return;
    }

    setAnalyzing(true);
    try {
      toast.info(`Analyzing ${photos.length} photo(s) with AI...`);

      // Clear old analysis first so we only see fresh Roboflow results
      for (const photo of photos) {
        await supabase
          .from('photos')
          .update({
            ai_analyzed: false,
            ai_damage_detected: false,
            ai_analysis_result: null,
            ai_damage_types: [],
          })
          .eq('id', photo.id);
      }

      // Analyze all photos
      for (const photo of photos) {
        const { error } = await supabase.functions.invoke('analyze-damage', {
          body: { photoId: photo.id, photoUrl: photo.url, orgId },
        });
        if (error) {
          console.error('Analysis error:', error);
          toast.error(`Failed to analyze ${photo.filename}`);
        }
      }

      toast.success('Analysis complete with AI');
      onPhotosUpdate();
    } catch (error: any) {
      console.error('Error during analysis:', error);
      toast.error('Failed to analyze photos');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (photos.length === 0) {
      toast.error("No photos to generate report");
      return;
    }

    setGeneratingPdf(true);
    try {
      toast.info("Generating PDF report...");
      
      const { data, error } = await supabase.functions.invoke('generate-measurement-pdf', {
        body: { 
          jobId,
          photos: photos.map(p => ({
            url: p.url,
            filename: p.filename,
            damage_detected: p.ai_damage_detected,
            damage_types: p.ai_damage_types
          }))
        }
      });

      if (error) throw error;

      if (data?.pdfUrl) {
        window.open(data.pdfUrl, '_blank');
        toast.success("PDF generated successfully!");
      }
    } catch (error: any) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const filteredPhotos = photos.filter((photo) => {
    if (filter === "damage") return photo.ai_damage_detected;
    if (filter === "clean") return !photo.ai_damage_detected;
    return true;
  });

  // Calculate damage statistics
  const damagePhotos = photos.filter(p => p.ai_damage_detected);
  const damageTypes = photos.reduce((acc, photo) => {
    if (photo.ai_damage_types) {
      photo.ai_damage_types.forEach(type => {
        acc[type] = (acc[type] || 0) + 1;
      });
    }
    return acc;
  }, {} as Record<string, number>);

  const getSeverity = (count: number, total: number): "destructive" | "default" | "secondary" => {
    const percentage = (count / total) * 100;
    if (percentage > 60) return "destructive";
    if (percentage > 30) return "default";
    return "secondary";
  };

  return (
    <>
      {/* AI Analysis Section */}
      {photos.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-warning" />
                Damage Detection
              </CardTitle>
            </CardHeader>
            <CardContent>
              {damagePhotos.length > 0 ? (
                <div className="space-y-3">
                  <div className="text-sm text-muted-foreground mb-4">
                    {damagePhotos.length} of {photos.length} photos show damage
                  </div>
                  {Object.entries(damageTypes).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between p-3 rounded-lg bg-muted">
                      <span className="text-sm font-medium capitalize">{type.replace('-', ' ')}</span>
                      <Badge variant={getSeverity(count, photos.length)}>
                        {count} photo{count > 1 ? 's' : ''}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>{photos.length === 0 ? 'Upload photos to see damage analysis' : 'No damage detected in uploaded photos'}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Damage Heatmap</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedPhoto ? (
                <div className="aspect-video bg-muted rounded-lg flex items-center justify-center p-4">
                  <img 
                    src={selectedPhoto.url} 
                    alt="Selected photo"
                    className="max-w-full max-h-full object-contain rounded"
                  />
                </div>
              ) : (
                <div className="aspect-video bg-muted rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors"
                     onClick={() => damagePhotos.length > 0 && setSelectedPhoto(damagePhotos[0])}>
                  <div className="text-center text-muted-foreground">
                    <Sparkles className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Click a damage photo to view</p>
                    <p className="text-xs mt-1">{damagePhotos.length === 0 ? 'No damage detected yet' : 'Or click here to view first damage photo'}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Upload and Filter Controls */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Photo Gallery</h3>
        <div className="flex gap-2">
          <Button
            onClick={handleAnalyzePhotos}
            disabled={analyzing || photos.length === 0}
            variant="secondary"
            className="gap-2"
          >
            {analyzing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {analyzing ? 'Analyzing...' : 'Analyze Photos (AI)'}
          </Button>
          <Button
            onClick={handleGeneratePdf}
            disabled={generatingPdf || photos.length === 0}
            variant="outline"
            className="gap-2"
          >
            {generatingPdf ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Generate PDF
          </Button>
          <div className="flex gap-1 border rounded-lg p-1">
            <Button
              size="sm"
              variant={filter === "all" ? "secondary" : "ghost"}
              onClick={() => setFilter("all")}
            >
              All ({photos.length})
            </Button>
            <Button
              size="sm"
              variant={filter === "damage" ? "secondary" : "ghost"}
              onClick={() => setFilter("damage")}
            >
              Damage ({photos.filter((p) => p.ai_damage_detected).length})
            </Button>
            <Button
              size="sm"
              variant={filter === "clean" ? "secondary" : "ghost"}
              onClick={() => setFilter("clean")}
            >
              Clean ({photos.filter((p) => !p.ai_damage_detected).length})
            </Button>
          </div>
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="gap-2"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Upload Photos
          </Button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileUpload}
        />
      </div>

      {filteredPhotos.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <Camera className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">
              {filter === "all"
                ? "No photos uploaded yet"
                : `No ${filter} photos found`}
            </p>
            {filter === "all" && (
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                Upload Your First Photo
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredPhotos.map((photo) => (
            <Card 
              key={photo.id} 
              className={`overflow-hidden cursor-pointer transition-all hover:shadow-lg ${selectedPhoto?.id === photo.id ? 'ring-2 ring-primary' : ''}`}
              onClick={() => handlePhotoClick(photo)}
            >
              <div className="aspect-square relative group">
                <img
                  src={photo.url}
                  alt={photo.filename}
                  className="w-full h-full object-cover"
                />
                {photo.ai_damage_detected && (
                  <Badge className="absolute top-2 left-2 bg-destructive text-destructive-foreground">
                    Damage
                  </Badge>
                )}
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => handleDeletePhoto(e, photo.id, photo.storage_path)}
                  title="Delete photo"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground truncate">
                    {photo.filename}
                  </p>
                  {photo.ai_damage_types && photo.ai_damage_types.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {photo.ai_damage_types.slice(0, 2).map((type, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {type}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          
          <PhotoMarkupDialog
            open={markupDialogOpen}
            onOpenChange={setMarkupDialogOpen}
            photo={currentMarkupPhoto}
            onSave={handleSaveAnnotations}
            allPhotos={filteredPhotos}
            onNavigate={(photoId) => {
              const photo = photos.find(p => p.id === photoId);
              if (photo) {
                setCurrentMarkupPhoto(photo);
              }
            }}
          />
        </>
      )}
    </>
  );
};
